import React from 'react'
import { Navigate } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { RootState } from '../app/store'
import { User } from '../types'

interface ProtectedRouteProps {
  children: React.ReactNode
  requiredRoles?: string[]
  requiredPermissions?: string[]
}

// Helper function to check if user has required permissions
const hasPermissions = (user: User | null, permissions: string[]): boolean => {
  if (!user) return false
  if (permissions.length === 0) return true
  
  // Check if user has any of the required permissions
  return permissions.some(permission => 
    user.permissions?.includes(permission)
  )
}

// Helper function to check if user has required groups (backward compatibility)
const hasRoles = (user: User | null, roles: string[]): boolean => {
  if (!user) return false
  if (roles.length === 0) return true
  
  // Check both role field (legacy) and groups
  const hasRoleMatch = roles.includes(user.role)
  const hasGroupMatch = user.groups?.some(group => roles.includes(group.name))
  
  return hasRoleMatch || hasGroupMatch
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ 
  children, 
  requiredRoles = [],
  requiredPermissions = []
}) => {
  const { isAuthenticated, user } = useSelector((state: RootState) => state.auth)

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />
  }

  // Check permissions first (preferred), then fall back to roles (backward compatibility)
  if (requiredPermissions.length > 0) {
    if (!hasPermissions(user, requiredPermissions)) {
      return <Navigate to="/dashboard" replace />
    }
  } else if (requiredRoles.length > 0) {
    if (!hasRoles(user, requiredRoles)) {
      return <Navigate to="/dashboard" replace />
    }
  }

  return <>{children}</>
}

export default ProtectedRoute
